import { useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { semanticColors, BORDER_RADIUS } from '@/theme/tokens';
import useAnalytics from '@/hooks/useAnalytics';

import {
  countSteps,
  currentLiveEntry,
  shouldCollapseLiveTrail,
  type NarrationEvent,
} from './NarrationTypes';
import NarrationTimeline from './NarrationTimeline';

/**
 * The research trail while the turn is still running.
 *
 * Up to `LIVE_TRAIL_COLLAPSE_AFTER` steps it is the full timeline. Past that it
 * collapses to the newest step, which replays its entrance as each new step
 * arrives so progress stays visible without the list taking over the chat. A
 * toggle opens the full list, and stays open as further steps land.
 */

interface LiveNarrationTrailProps {
  events: NarrationEvent[];
  /** The answer's language when it differs from the UI's — see `trailLanguageFor`. */
  language?: string;
  /**
   * Set to `true` when the toggle still has focus as the trail unmounts, so
   * the control that replaces it (`ReasoningDisclosure`) can take focus rather
   * than leaving it on the document body.
   */
  focusHandoffRef?: RefObject<boolean>;
}

/** Tracked on expand only, mirroring `Chat Reasoning Expanded` on the kept trail. */
const LIVE_STEPS_EXPANDED_EVENT = 'Chat Live Steps Expanded';

export default function LiveNarrationTrail({
  events,
  language,
  focusHandoffRef,
}: LiveNarrationTrailProps) {
  // The toggle is UI chrome, so it follows the UI language like the kept
  // trail's disclosure label; the steps themselves follow `language`.
  const { t } = useTranslation('chat');
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);

  // A layout cleanup runs before React removes the node, while the browser
  // still reports it as focused.
  useLayoutEffect(() => {
    const toggle = toggleRef;
    return () => {
      if (focusHandoffRef && toggle.current && toggle.current === document.activeElement) {
        focusHandoffRef.current = true;
      }
    };
  }, [focusHandoffRef]);

  const collapsible = shouldCollapseLiveTrail(events);
  const current = collapsible && !open ? currentLiveEntry(events) : undefined;
  const stepCount = countSteps(events);

  const handleToggle = () => {
    if (!open) track(LIVE_STEPS_EXPANDED_EVENT, { stepCount });
    setOpen((v) => !v);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      {collapsible && (
        <Box
          component="button"
          type="button"
          ref={toggleRef}
          onClick={handleToggle}
          aria-expanded={open}
          aria-controls={panelId}
          sx={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            // Pulled left by its own padding so the chevron lines up with the
            // step badges below it.
            ml: -1,
            px: 1,
            py: 0.5,
            border: 'none',
            borderRadius: BORDER_RADIUS,
            backgroundColor: 'transparent',
            color: semanticColors.textCaption,
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 12,
            // Hover only where it exists: on touch, :hover sticks after a tap and
            // leaves the toggle shaded as if it were still pressed.
            '@media (hover: hover)': {
              '&:hover': { backgroundColor: semanticColors.surfaceSubtle },
            },
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimary}`,
              outlineOffset: 2,
            },
          }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              display: 'inline-flex',
              flexShrink: 0,
              transition: 'transform 0.15s ease-out',
              transform: open ? 'rotate(90deg)' : 'none',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            <ChevronRightIcon style={{ width: 14, height: 14 }} />
          </Box>
          {/* One label in both states: `aria-expanded` carries open/closed. */}
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'inherit' }}>
            {t('narration.live.stepsSoFar', { count: stepCount })}
          </Typography>
        </Box>
      )}

      {/* One live region for the whole turn, so the swap from the full list to
          the current step (and back) happens inside it and each new step is
          still announced. A region mounted already filled would stay silent. */}
      <Box id={panelId} role="status" aria-live="polite" aria-atomic={false} sx={{ minWidth: 0 }}>
        {current ? (
          <Box
            key={current.index}
            sx={{
              animation: 'liveStepIn 0.45s ease-out',
              '@keyframes liveStepIn': {
                '0%': { opacity: 0, transform: 'translateY(6px)' },
                '100%': { opacity: 1, transform: 'none' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            <NarrationTimeline events={[current.event]} liveRegion={false} language={language} />
          </Box>
        ) : (
          <NarrationTimeline events={events} liveRegion={false} language={language} />
        )}
      </Box>
    </Box>
  );
}
